import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MfaCodeRequest, mfaCodeRequestSchema, MfaRecoveryCodes, ReadUser } from '@tinct/core-api-models';
import { useActivateMfa } from '@tinct/core-api';
import { handleApiError } from '@tinct/web-form';
import { toast } from 'sonner';

/**
 * Confirms a pending enrolment with the first code from the authenticator app. A wrong code
 * (400), a lock after too many attempts (429) and a lost enrolment (404) all come back as a
 * form-level message from the API; nothing else is worth distinguishing for the user.
 */
export const useActivateMfaForm = (user: ReadUser, onActivated: (codes: MfaRecoveryCodes) => void) => {
  const form = useForm<MfaCodeRequest>({
    resolver: zodResolver(mfaCodeRequestSchema),
    defaultValues: { code: '' },
  });

  const activateMfa = useActivateMfa({
    onSuccess: (codes) => {
      toast.success('Two-factor authentication enabled');
      onActivated(codes);
    },
    onError: (error) => handleApiError(error, form),
  });

  const handleSubmit = form.handleSubmit((data) => {
    activateMfa.mutate({
      userId: user.id as string,
      data: { code: data.code.trim() },
    });
  });

  return { form, handleSubmit, isLoading: activateMfa.isPending };
};
